/**
 * Pestaña Resumen del repositorio de Redes Sociales: KPIs del periodo contra el periodo
 * anterior, tendencia mensual por red y tabla por perfil con la foto de seguidores al
 * inicio y al cierre del periodo.
 */
import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ink, studio, FilterPill } from '../admin/studioUi';
import { downloadCsv } from './repoApi';
import {
    NETWORK_COLORS, addMonths, change, engagementRate, followerSnapshotDelta, formatCompact, formatNumber,
    formatPct, formatSigned, inPeriod, matchesSelection, monthsBetween, periodLabel, previousPeriod,
    profileKey, sumRows,
} from './socialRepoUtils';
import NetworkDot from './NetworkDot';
import MobileCards from './MobileCards';
import InfoTip from './InfoTip';
import { rt, useFitColumns, HiddenColumnsNote } from './repoTable';
import LoadingBlock from './LoadingBlock';
import { evaluateKpi, formatKpi } from './customKpis';

const SUM_METRICS = ['Publicaciones', 'Impresiones', 'Alcance', 'Vistas', 'Interacciones'];
const TREND_METRICS = [
    { id: 'Publicaciones', source: 'posts' },
    { id: 'Impresiones', source: 'posts' },
    { id: 'Alcance', source: 'posts' },
    { id: 'Vistas', source: 'posts' },
    { id: 'Interacciones', source: 'posts' },
    { id: 'Neto', source: 'followers', label: 'Seguidores netos' },
    { id: 'Seguidores al Cierre', source: 'followers', label: 'Seguidores al cierre' },
];

function Kpi({ label, value, title, delta, hint, custom, info, infoHow }) {
    const tone = delta == null ? ink.faint : delta >= 0 ? '#137333' : ink.danger;
    return (
        <div style={{ ...styles.kpi, ...(custom ? styles.kpiCustom : {}) }} title={title || (custom ? `Tu KPI: ${hint}` : undefined)}>
            <span style={{ ...studio.statLabel, display: 'inline-flex', alignItems: 'center' }}>
                {label}
                {info && <InfoTip term={info} how={infoHow} />}
            </span>
            <span style={styles.kpiValue}>{value}</span>
            <span style={{ ...styles.kpiDelta, color: tone }}>
                {delta == null ? (hint || 'sin comparación') : `${delta >= 0 ? '▲' : '▼'} ${formatPct(Math.abs(delta))} vs. periodo anterior`}
                {delta != null && <InfoTip term="comparacion" />}
            </span>
        </div>
    );
}

const SocialSummary = ({ loading, postsMonthly, followersMonthly, followerSnapshots, monthlyMerged = [], kpis = [], onToggleExclude, selection, period, narrow }) => {
    const [trendMetric, setTrendMetric] = useState('Interacciones');
    const [sort, setSort] = useState({ key: 'Interacciones', dir: 'desc' });

    const prev = useMemo(() => previousPeriod(period), [period]);
    const posts = useMemo(() => postsMonthly.filter(row => matchesSelection(row, selection)), [postsMonthly, selection]);
    const followers = useMemo(() => followersMonthly.filter(row => matchesSelection(row, selection)), [followersMonthly, selection]);
    const snapshots = useMemo(() => followerSnapshots.filter(row => matchesSelection(row, selection)), [followerSnapshots, selection]);
    const merged = useMemo(() => monthlyMerged.filter(row => matchesSelection(row, selection)), [monthlyMerged, selection]);
    const customCards = useMemo(() => {
        const cur = inPeriod(merged, period);
        const old = inPeriod(merged, prev);
        return kpis.map(kpi => ({ kpi, value: evaluateKpi(kpi, cur), prevValue: old.length ? evaluateKpi(kpi, old) : null }));
    }, [kpis, merged, period, prev]);

    const summary = useMemo(() => {
        const cur = inPeriod(posts, period);
        const old = inPeriod(posts, prev);
        const curF = inPeriod(followers, period);
        const oldF = inPeriod(followers, prev);
        const sums = sumRows(cur, SUM_METRICS);
        const prevSums = sumRows(old, SUM_METRICS);
        const net = sumRows(curF, ['Neto']).Neto;
        const prevNet = sumRows(oldF, ['Neto']).Neto;
        const deltas = followerSnapshotDelta(snapshots, period);
        let photoEnd = 0;
        let photoDelta = 0;
        let photoDeltaBase = 0;
        deltas.forEach((item) => {
            photoEnd += item.end.value;
            if (item.delta != null) {
                photoDelta += item.delta;
                photoDeltaBase += item.start.value;
            }
        });
        /* Sin fotos en el periodo (antes de que existiera el archivo) se usa el cierre mensual. */
        const closingMonth = new Map();
        curF.forEach((row) => {
            const key = profileKey(row);
            const prevRow = closingMonth.get(key);
            if (!prevRow || row.Mes > prevRow.Mes) closingMonth.set(key, row);
        });
        const monthlyClose = Array.from(closingMonth.values()).reduce((acc, row) => acc + (Number(row['Seguidores al Cierre']) || 0), 0);
        const rate = engagementRate(cur);
        const prevRate = engagementRate(old);
        return {
            sums, prevSums, net, prevNet, deltas, rate, prevRate,
            followersValue: deltas.size ? photoEnd : monthlyClose,
            followersFromPhotos: deltas.size > 0,
            photoDelta: deltas.size ? photoDelta : null,
            photoDeltaPct: photoDeltaBase ? photoDelta / photoDeltaBase : null,
            hasPrev: old.length > 0,
        };
    }, [posts, followers, snapshots, period, prev]);

    /* Tendencia: el periodo; si es corto, los 6 meses que terminan en su cierre para dar contexto. */
    const trend = useMemo(() => {
        const months = monthsBetween(period.from, period.to).length >= 3
            ? monthsBetween(period.from, period.to)
            : monthsBetween(addMonths(period.to, -5), period.to);
        const customKpi = kpis.find(k => `kpi:${k.id}` === trendMetric);
        if (customKpi) {
            /* Un KPI propio puede ser un cociente: se calcula por mes y red, sin apilar. */
            const groups = new Map();
            merged.forEach((row) => {
                if (!months.includes(row.Mes)) return;
                const key = `${row.Mes}|${row.Red}`;
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key).push(row);
            });
            const networks = new Set();
            const data = months.map((month) => {
                const point = { Mes: month };
                groups.forEach((rows, key) => {
                    const [m, red] = key.split('|');
                    if (m !== month) return;
                    networks.add(red);
                    point[red] = evaluateKpi(customKpi, rows);
                });
                return point;
            });
            return { data, networks: Array.from(networks), label: customKpi.name, kpi: customKpi };
        }
        const spec = TREND_METRICS.find(m => m.id === trendMetric) || TREND_METRICS[0];
        const rows = spec.source === 'posts' ? posts : followers;
        const networks = new Set();
        const byMonth = new Map(months.map(m => [m, { Mes: m }]));
        rows.forEach((row) => {
            const bucket = byMonth.get(row.Mes);
            if (!bucket) return;
            networks.add(row.Red);
            bucket[row.Red] = (bucket[row.Red] || 0) + (Number(row[spec.id]) || 0);
        });
        return { data: Array.from(byMonth.values()), networks: Array.from(networks), label: spec.label || spec.id, kpi: null };
    }, [posts, followers, merged, kpis, trendMetric, period]);

    const profileRows = useMemo(() => {
        const cur = inPeriod(posts, period);
        const curF = inPeriod(followers, period);
        const map = new Map();
        const ensure = (row) => {
            const key = profileKey(row);
            if (!map.has(key)) map.set(key, { key, Red: row.Red, Perfil: row.Perfil, rows: [], net: 0 });
            return map.get(key);
        };
        cur.forEach(row => ensure(row).rows.push(row));
        curF.forEach((row) => { ensure(row).net += Number(row.Neto) || 0; });
        const mergedByProfile = new Map();
        inPeriod(merged, period).forEach((row) => {
            const key = profileKey(row);
            if (!mergedByProfile.has(key)) mergedByProfile.set(key, []);
            mergedByProfile.get(key).push(row);
        });
        summary.deltas.forEach((item, key) => {
            const [Red, ...rest] = key.split('|');
            ensure({ Red, Perfil: rest.join('|') });
        });
        return Array.from(map.values()).map((item) => {
            const sums = sumRows(item.rows, SUM_METRICS);
            const photo = summary.deltas.get(item.key) || null;
            return {
                ...item,
                ...sums,
                Seguidores: photo ? photo.end.value : null,
                photo,
                Neto: item.net,
                Tasa: engagementRate(item.rows),
                ...Object.fromEntries(kpis.map(kpi => [`kpi:${kpi.id}`, evaluateKpi(kpi, mergedByProfile.get(item.key) || [])])),
            };
        });
    }, [posts, followers, merged, kpis, period, summary.deltas]);

    const sortedProfiles = useMemo(() => {
        const factor = sort.dir === 'asc' ? 1 : -1;
        const value = (row) => {
            if (sort.key === 'Perfil' || sort.key === 'Red') return String(row[sort.key]);
            if (sort.key === 'Delta') return row.photo?.delta ?? -Infinity;
            if (sort.key.startsWith('kpi:')) return row[sort.key] ?? -Infinity;
            return row[sort.key] ?? -Infinity;
        };
        return [...profileRows].sort((a, b) => {
            const va = value(a);
            const vb = value(b);
            if (typeof va === 'string') return va.localeCompare(vb, 'es') * factor;
            return (va - vb) * factor;
        });
    }, [profileRows, sort]);

    const deltaColor = v => (v > 0 ? '#137333' : v < 0 ? ink.danger : ink.text);
    const columns = [
        {
            key: 'Perfil', label: 'Perfil', min: 170,
            render: row => (
                <span style={styles.profileCell}>
                    <span style={styles.redCell}><NetworkDot red={row.Red} /><strong>{row.Perfil}</strong></span>
                    <span style={styles.subText}>{row.Red}</span>
                </span>
            ),
        },
        {
            key: 'Seguidores', label: 'Seguidores (foto)', num: true, info: 'seguidoresFoto', min: 88, priority: 1,
            title: row => (row.photo ? `Foto del ${row.photo.end.date}` : 'Sin foto en el periodo'),
            render: row => (row.photo ? formatCompact(row.photo.end.value) : <span style={styles.muted}>sin foto</span>),
        },
        {
            key: 'Delta', label: 'Cambio (foto)', num: true, info: 'cambioFoto', min: 92, priority: 2,
            title: row => (row.photo?.start ? `Desde la foto del ${row.photo.start.date}` : ''),
            color: row => deltaColor(row.photo?.delta),
            render: row => (row.photo?.delta != null
                ? <>{formatSigned(row.photo.delta)}<span style={styles.subNum}>{formatPct(row.photo.deltaPct)}</span></>
                : <span style={styles.muted}>—</span>),
        },
        { key: 'Neto', label: 'Neto (mov.)', num: true, info: 'neto', min: 74, priority: 6, render: row => formatSigned(row.Neto) },
        { key: 'Publicaciones', label: 'Posts', num: true, info: 'Publicaciones', min: 56, priority: 3, render: row => formatNumber(row.Publicaciones) },
        { key: 'Alcance', label: 'Alcance', num: true, info: 'Alcance', min: 72, priority: 5, title: row => formatNumber(row.Alcance), render: row => formatCompact(row.Alcance) },
        { key: 'Impresiones', label: 'Impres.', num: true, info: 'Impresiones', min: 72, priority: 8, title: row => formatNumber(row.Impresiones), render: row => formatCompact(row.Impresiones) },
        { key: 'Interacciones', label: 'Interacc.', num: true, info: 'Interacciones', min: 74, priority: 4, title: row => formatNumber(row.Interacciones), render: row => formatCompact(row.Interacciones) },
        { key: 'Tasa', label: 'Tasa int.', num: true, info: 'tasa', min: 68, priority: 4, render: row => formatPct(row.Tasa, 2) },
        ...kpis.map(kpi => ({
            key: `kpi:${kpi.id}`, label: kpi.name, num: true, kpi, info: 'kpiPropio', min: 80, priority: 7,
            color: () => ink.accentText, render: row => formatKpi(kpi, row[`kpi:${kpi.id}`]),
        })),
    ];
    const [tableRef, visibleColumns, hiddenColumns] = useFitColumns(columns, { reserve: onToggleExclude ? 64 : 0 });
    const toggleSort = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'Perfil' || key === 'Red' ? 'asc' : 'desc' }));

    const exportProfiles = () => downloadCsv(`redes-sociales-perfiles-${period.from}_${period.to}.csv`, [
        { label: 'Red', value: r => r.Red },
        { label: 'Perfil', value: r => r.Perfil },
        { label: 'Seguidores inicio', value: r => r.photo?.start?.value ?? '' },
        { label: 'Fecha foto inicio', value: r => r.photo?.start?.date ?? '' },
        { label: 'Seguidores cierre', value: r => r.photo?.end?.value ?? '' },
        { label: 'Fecha foto cierre', value: r => r.photo?.end?.date ?? '' },
        { label: 'Cambio (foto)', value: r => r.photo?.delta ?? '' },
        { label: 'Neto (movimiento)', value: r => r.Neto },
        ...SUM_METRICS.map(m => ({ label: m, value: r => r[m] })),
        { label: 'Tasa de interacción', value: r => (r.Tasa == null ? '' : r.Tasa.toFixed(4)) },
        ...kpis.map(kpi => ({ label: kpi.name, value: r => (r[`kpi:${kpi.id}`] == null ? '' : r[`kpi:${kpi.id}`]) })),
    ], sortedProfiles);

    if (loading) return <LoadingBlock />;

    const d = (key) => (summary.hasPrev ? change(summary.sums[key], summary.prevSums[key]) : null);
    return (
        <div style={styles.wrap}>
            <div style={{ ...styles.kpiGrid, gridTemplateColumns: narrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(165px, 1fr))' }}>
                <Kpi
                    label={summary.followersFromPhotos ? 'Seguidores (foto al cierre)' : 'Seguidores al cierre'}
                    info="seguidoresFoto"
                    value={formatCompact(summary.followersValue)}
                    title={formatNumber(summary.followersValue)}
                    delta={null}
                    hint={summary.photoDelta != null
                        ? `${formatSigned(summary.photoDelta)} (${formatPct(summary.photoDeltaPct)}) en el periodo`
                        : 'del cierre mensual; sin fotos en el periodo'}
                />
                <Kpi
                    label="Seguidores netos"
                    info="neto"
                    value={formatSigned(summary.net)}
                    delta={summary.hasPrev ? change(summary.net, summary.prevNet) : null}
                />
                <Kpi label="Publicaciones" info="Publicaciones" value={formatNumber(summary.sums.Publicaciones)} delta={d('Publicaciones')} />
                <Kpi label="Alcance" info="Alcance" value={formatCompact(summary.sums.Alcance)} title={formatNumber(summary.sums.Alcance)} delta={d('Alcance')} />
                <Kpi label="Impresiones" info="Impresiones" value={formatCompact(summary.sums.Impresiones)} title={formatNumber(summary.sums.Impresiones)} delta={d('Impresiones')} />
                <Kpi label="Vistas" info="Vistas" value={formatCompact(summary.sums.Vistas)} title={formatNumber(summary.sums.Vistas)} delta={d('Vistas')} />
                <Kpi label="Interacciones" info="Interacciones" value={formatCompact(summary.sums.Interacciones)} title={formatNumber(summary.sums.Interacciones)} delta={d('Interacciones')} />
                <Kpi
                    label="Tasa de interacción"
                    info="tasa"
                    value={formatPct(summary.rate, 2)}
                    delta={summary.hasPrev ? change(summary.rate, summary.prevRate) : null}
                    hint="interacciones / alcance"
                />
                {customCards.map(({ kpi, value, prevValue }) => (
                    <Kpi
                        key={kpi.id}
                        label={kpi.name}
                        value={formatKpi(kpi, value)}
                        delta={summary.hasPrev ? change(value, prevValue) : null}
                        hint={kpi.formula}
                        info="kpiPropio"
                        infoHow={`Fórmula: ${kpi.formula}. Se suma cada campo con los filtros activos y luego se aplica la fórmula.`}
                        custom
                    />
                ))}
            </div>

            <section style={studio.card}>
                <div style={styles.cardHead}>
                    <div>
                        <h3 style={styles.cardTitle}>Tendencia mensual<InfoTip what="Evolución mes a mes de la métrica elegida, apilada por red." how="Suma mensual de los archivos mensuales. Si el periodo es menor a 3 meses, se muestran los 6 meses que terminan en el cierre para dar contexto. Los KPIs propios se calculan por mes y red y no se apilan." /></h3>
                        <p style={studio.hint}>{trend.label} por red · {periodLabel({ from: trend.data[0]?.Mes || period.from, to: period.to })}</p>
                    </div>
                    <FilterPill value={trendMetric} onChange={setTrendMetric} style={{ minWidth: 190 }}>
                        {TREND_METRICS.map(m => <option key={m.id} value={m.id}>{m.label || m.id}</option>)}
                        {kpis.length > 0 && (
                            <optgroup label="Tus KPIs">
                                {kpis.map(kpi => <option key={kpi.id} value={`kpi:${kpi.id}`}>{kpi.name}</option>)}
                            </optgroup>
                        )}
                    </FilterPill>
                </div>
                <div style={{ width: '100%', height: narrow ? 240 : 300 }}>
                    <ResponsiveContainer>
                        <BarChart data={trend.data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={ink.line} vertical={false} />
                            <XAxis dataKey="Mes" tick={{ fontSize: 12, fill: ink.muted }} tickFormatter={m => periodLabel({ from: m, to: m })} />
                            <YAxis
                                tick={{ fontSize: 12, fill: ink.muted }}
                                tickFormatter={v => (trend.kpi?.format === 'percent' ? `${(v * 100).toFixed(0)}%` : trend.kpi?.format === 'decimal' ? v.toFixed(1) : formatCompact(v))}
                                width={56}
                            />
                            <Tooltip
                                formatter={(value, name) => [trend.kpi ? formatKpi(trend.kpi, value) : formatNumber(value), name]}
                                labelFormatter={m => periodLabel({ from: m, to: m })}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {trend.networks.map(red => (
                                <Bar key={red} dataKey={red} stackId={trend.kpi ? undefined : 'red'} fill={NETWORK_COLORS[red] || ink.accent} maxBarSize={48} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>

            <section style={studio.card}>
                <div style={styles.cardHead}>
                    <div>
                        <h3 style={styles.cardTitle}>Perfiles</h3>
                        <p style={studio.hint}>
                            {sortedProfiles.length} perfiles · seguidores según la foto diaria más cercana al inicio y al cierre del periodo
                        </p>
                    </div>
                    <button type="button" onClick={exportProfiles} style={studio.ghostBtn} disabled={!sortedProfiles.length}>Exportar CSV</button>
                </div>
                {narrow ? (
                    <MobileCards
                        items={sortedProfiles}
                        keyOf={row => row.key}
                        titleOf={row => <><NetworkDot red={row.Red} />{row.Perfil}</>}
                        subtitleOf={row => (row.photo ? `${row.Red} · ${formatNumber(row.photo.end.value)} seguidores (foto ${row.photo.end.date})` : `${row.Red} · sin foto en el periodo`)}
                        metricsOf={row => [
                            { label: 'Cambio (foto)', value: row.photo?.delta != null ? formatSigned(row.photo.delta) : '—', color: row.photo?.delta > 0 ? '#137333' : row.photo?.delta < 0 ? ink.danger : undefined },
                            { label: 'Neto', value: formatSigned(row.Neto) },
                            { label: 'Posts', value: formatNumber(row.Publicaciones) },
                            { label: 'Alcance', value: formatCompact(row.Alcance) },
                            { label: 'Interacc.', value: formatCompact(row.Interacciones) },
                            { label: 'Tasa int.', value: formatPct(row.Tasa, 2) },
                            ...kpis.map(kpi => ({ label: kpi.name, value: formatKpi(kpi, row[`kpi:${kpi.id}`]), color: ink.accentText })),
                        ]}
                        actionOf={onToggleExclude ? (row => <button type="button" style={styles.omitBtn} onClick={() => onToggleExclude([row.key])}>Omitir</button>) : undefined}
                        empty="Sin datos para estos filtros."
                    />
                ) : (
                <div ref={tableRef}>
                    <table style={rt.table}>
                        <thead>
                            <tr>
                                {visibleColumns.map(col => (
                                    <th
                                        key={col.key}
                                        onClick={() => toggleSort(col.key)}
                                        style={{ ...rt.th, cursor: 'pointer', textAlign: col.num ? 'right' : 'left', ...(col.kpi ? { color: ink.accentText } : {}) }}
                                    >
                                        {col.label}{sort.key === col.key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                                        {col.info && <InfoTip term={col.info} how={col.kpi ? `Fórmula: ${col.kpi.formula}.` : undefined} />}
                                    </th>
                                ))}
                                {onToggleExclude && <th style={{ ...rt.th, width: 60 }} />}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedProfiles.map(row => (
                                <tr key={row.key}>
                                    {visibleColumns.map(col => (
                                        <td
                                            key={col.key}
                                            title={col.title ? col.title(row) : undefined}
                                            style={{ ...(col.num ? rt.num : rt.td), ...(col.color ? { color: col.color(row) } : {}) }}
                                        >
                                            {col.render(row)}
                                        </td>
                                    ))}
                                    {onToggleExclude && (
                                        <td style={{ ...rt.td, textAlign: 'right' }}>
                                            <button type="button" style={styles.omitBtn} title="Quitar este perfil de todos los cálculos" onClick={() => onToggleExclude([row.key])}>Omitir</button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {!sortedProfiles.length && (
                                <tr><td colSpan={visibleColumns.length + 1} style={{ ...rt.td, textAlign: 'center', color: ink.faint }}>Sin datos para estos filtros.</td></tr>
                            )}
                        </tbody>
                    </table>
                    <HiddenColumnsNote hidden={hiddenColumns} />
                </div>
                )}
            </section>
        </div>
    );
};

const styles = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 18 },
    kpiGrid: { display: 'grid', gap: 10 },
    kpi: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '10px 14px',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderLeftWidth: 3,
        borderLeftColor: ink.accent,
        borderRadius: 12,
        minWidth: 0,
    },
    omitBtn: {
        padding: '3px 9px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.muted,
        fontSize: '0.72rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    kpiCustom: { borderLeftColor: '#F9AB00', backgroundColor: '#fffdf5' },
    kpiValue: { fontSize: '1.3rem', fontWeight: 600, color: ink.text, letterSpacing: '-0.03em', lineHeight: 1.15 },
    kpiDelta: { fontSize: '0.76rem' },
    cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
    cardTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 600, color: ink.text },
    tableScroll: { overflowX: 'auto' },
    numTd: { ...studio.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
    redCell: { display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
    muted: { color: ink.faint, fontSize: '0.82rem' },
    profileCell: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
    subText: { fontSize: '0.7rem', color: ink.faint, marginLeft: 14 },
    subNum: { display: 'block', fontSize: '0.7rem', opacity: 0.8 },
};

export default SocialSummary;
